import { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { createColumns } from "../../../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../../../constants/datatableStyles";
import CustomPagination from "../../../../Components/CustomPagination";
import PageTitle from "../../../../Components/UI/PageTitle";
import RowLoader from "../../../../Components/UI/RowLoader";
import Flatpickr from "react-flatpickr";
import DropdownSelect from "../../../../Components/DropdownSelect";
import {
  getAvailableSubscriptionDurationsForAdmin,
  getRenewSubscriptionsReports,
} from "../../../../apis";
import { transformKeys } from "../../../../_helper";
import axios from "axios";

const RenewSubscriptionReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isExportLoading, setExportLoading] = useState(false);

  const handleData = data?.data?.subscriptions?.data?.map((d) => {
    return transformKeys({
      ...d,
      is_Active: d.isActive === 1 ? "Yes" : "No",
    });
  });

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [filterByRenewDuration, setFilterByRenewDuration] = useState("");
  const [selectedRenewDuration, setSelectedRenewDuration] = useState("");

  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );
  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );

  useEffect(() => {
    getAvailableSubscriptionDurationsForAdmin()
      .then((response) => {
        setFilterByRenewDuration(response.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    getRenewSubscriptionsReports(
      searchTerm,
      startDate,
      endDate,
      selectedRenewDuration?.id,
      currentPage
    )
      .then((response) => {
        setData(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [searchTerm, startDate, endDate, selectedRenewDuration?.id, currentPage]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    params.set("page", currentPage);
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() });
  }, [searchTerm, currentPage, sortKey, sortDirection, navigate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, selectedRenewDuration]);

  const handleFilter = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = (column, direction) => {
    setSortKey(column.sortKey);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page on sort change
  };

  const hiddenColumns = [
    "id",
    "action",
    "must_verify_email",
    "client_id",
    "can_be_deleted",
  ]; // Add column keys you want to hide

  const columns = useMemo(() => {
    return createColumns(handleData, [], hiddenColumns, true).map((col) => ({
      ...col,
      // sortable: col.name === "Name" || col.name === "Joined Date",
      sortable: false,
      width:
        col.name === "Num" ? "60px" : col.name === "Name" ? "150px" : "1fr",
    }));
  }, [handleData]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const { data } = await axios.get(
        `admin/reports/subscriptions/export-excel`,
        {
          responseType: "blob",
        }
      );

      const fileName = `Reports_Subscriptions_Report_${new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "")}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data:", error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <PageTitle title="Renew Subscription Report" />

      <div className="bg-white flex flex-col mt-4">
        <div className="bg-white shadow-lg rounded-lg flex flex-col p-2 gap-2 mb-5">
          <div className="relative">
            <div className="tw-mb-2">
              <input
                type="text"
                onChange={handleFilter}
                value={searchTerm}
                className="border border-blue-300 rounded-lg p-2 col-md-12 tw-mb-2"
                placeholder="Search by name, email and phone"
              />
            </div>
            <div className="tw-grid lg:tw-grid-cols-3 tw-gap-4 tw-grid-cols-1 tw-items-end tw-mb-4">
              <DropdownSelect
                options={filterByRenewDuration}
                placeholder={"Duration Plan"}
                labelKey="duration_name"
                currentOption={selectedRenewDuration}
                setCurrentOption={setSelectedRenewDuration}
              />

              <div className="tw-flex tw-items-center tw-relative">
                <span className="tw-text-[#0B2787] tw-font-[400] tw-whitespace-nowrap tw-w-28 tw-pe-2">
                  Date From:
                </span>
                <Flatpickr
                  value={startDate}
                  onChange={([date]) => setStartDate(date)}
                  className="border border-gray-300 rounded-md p-2 !tw-bg-[#EFEFEF]"
                />
                {startDate && (
                  <button
                    onClick={() => setStartDate(null)}
                    className="tw-p-2 tw-text-[#797777] tw-absolute -tw-end-2 tw-text-lg tw-font-extrabold"
                  >
                    x
                  </button>
                )}
              </div>
              <div className="tw-flex tw-items-center tw-relative">
                <span className="tw-text-[#0B2787] tw-font-[400] tw-whitespace-nowrap tw-w-28">
                  Date To:
                </span>
                <Flatpickr
                  value={endDate}
                  onChange={([date]) => setEndDate(date)}
                  className="border border-gray-300 rounded-md p-2 !tw-bg-[#EFEFEF]"
                />
                {endDate && (
                  <button
                    onClick={() => setEndDate(null)}
                    className="tw-p-2 tw-text-[#797777] tw-absolute -tw-end-2 tw-text-lg tw-font-extrabold"
                  >
                    x
                  </button>
                )}
              </div>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={handleData}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={false}
            onSort={handleSortChange}
            progressComponent={<RowLoader />}
            sortServer
          />
          <div className="tw-text-[15px] tw-font-bold tw-text-gray-600 tw-text-right tw-pr-4 tw-my-2">
            Total Amounts:{" "}
            <span className="!tw-font-bold">
              {data?.data?.totalAmount ?? 0}
            </span>
          </div>
          <div className="flex justify-center">
            <CustomPagination
              totalPages={data?.data?.subscriptions?.last_page}
              currentPage={data?.data?.subscriptions?.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      {handleData?.length > 0 && (
        <button
          className={`tw-border tw-bg-green-600/20 tw-flex tw-justify-center tw-w-[200px] tw-h-10 tw-text-center tw-border-green-500 tw-px-4 tw-py-2 tw-rounded-lg tw-items-center tw-gap-2 tw-transition-all hover:tw-bg-green-600/30`}
          onClick={handleExport}
        >
          {isExportLoading ? (
            <div className="tw-flex tw-justify-center">
              <RowLoader paddingStyle="tw-py-0" />
            </div>
          ) : (
            <>
              <img
                src="/imgs/excelIcon.png"
                className="tw-size-5"
                alt="Export"
              />
              <span>Export as excel file</span>
            </>
          )}
        </button>
      )}
    </>
  );
};

export default RenewSubscriptionReport;
